import { generateId } from '../../utils';
import { NodeFactory } from '../../services/node/NodeFactory';
import {
  applyCatalogToNode,
  applyCatalogToParticipant,
  applyWorkspaceToState,
  buildC4Workspace,
  enrollNode,
  mergeIntoCatalog,
  syncCatalogAcrossDocument,
  type CatalogDocument,
} from '../../utils/catalog';
import { DEFAULT_LAYER, ROOT_GRAPH_ID } from './pageSlice';
import type {
  CatalogComponent,
  DiagramType,
  Edge,
  Flow,
  Layer,
  NodeGroup,
  SequenceFragment,
  SequenceMessage,
  SequenceParticipant,
  SequenceParticipantGroup,
} from '../../types';

export interface CatalogMutableState extends CatalogDocument {
  activePage: string;
  activeGraphId: string;
  diagramType: DiagramType;
  edges: Edge[];
  groups: NodeGroup[];
  layers: Layer[];
  viewport: { x: number; y: number; scale: number };
  messages: SequenceMessage[];
  fragments: SequenceFragment[];
  participantGroups: SequenceParticipantGroup[];
  seqLifelineLength: number;
  selectedItems: string[];
  graphNavStack: Array<{
    graphId: string;
    graphLabel: string;
    returnViewport: { x: number; y: number; scale: number };
  }>;
  groupNavStack: Array<{
    groupId: string;
    groupLabel: string;
    returnViewport: { x: number; y: number; scale: number };
  }>;
  activeLayerId: string;
  flows: Flow[];
}

export interface CatalogActions {
  updateCatalog: (catalogId: string, patch: Partial<CatalogComponent>) => void;
  placeCatalogInstance: (catalogId: string, position?: { x: number; y: number }) => string | undefined;
  createChildView: (nodeId: string, options?: { name?: string; diagramType?: DiagramType }) => string | undefined;
  createC4Workspace: (systemName?: string) => { contextPageId: string; containersPageId: string; sequencePageId: string };
}

export type CatalogStateUpdater<TState extends CatalogMutableState> = (
  mutator: (state: TState) => void,
) => void;

export function createCatalogActions<TState extends CatalogMutableState>(
  applyUpdate: CatalogStateUpdater<TState>,
): CatalogActions {
  return {
    updateCatalog: (catalogId, patch) => {
      applyUpdate((state) => {
        const entry = state.catalog.find(item => item.id === catalogId);
        if (!entry) return;
        mergeIntoCatalog(entry, patch);
        syncCatalogAcrossDocument(state, catalogId);
      });
    },

    placeCatalogInstance: (catalogId, position) => {
      let createdId: string | undefined;
      applyUpdate((state) => {
        const entry = state.catalog.find(item => item.id === catalogId);
        if (!entry) return;
        if (state.diagramType === 'sequence') {
          const order = state.participants.length;
          const last = state.participants.reduce<SequenceParticipant | null>(
            (acc, item) => (!acc || item.x > acc.x ? item : acc),
            null,
          );
          const participant: SequenceParticipant = {
            id: generateId('participant'),
            type: entry.type,
            label: entry.name,
            color: entry.color ?? '#6366f1',
            order,
            x: last ? last.x + 200 : 150,
            catalogId: entry.id,
            description: entry.description,
            links: entry.links?.map(link => ({ ...link })),
          };
          applyCatalogToParticipant(participant, entry);
          state.participants.push(participant);
          createdId = participant.id;
          return;
        }
        const blueprint = NodeFactory.create(entry.type, state.nodes.filter(n => n.type === entry.type).length);
        const node = {
          id: generateId('node'),
          type: entry.type,
          label: entry.name,
          position: position ?? { x: 120 + state.nodes.length * 24, y: 180 },
          size: blueprint.size,
          sockets: blueprint.sockets,
          data: { ...blueprint.data, ...(entry.color ? { color: entry.color } : {}) },
          catalogId: entry.id,
          meta: {
            description: entry.description,
            owner: entry.owner,
            status: entry.status,
            links: entry.links?.map(link => ({ ...link })),
          },
          layerId: state.activeLayerId === 'default' ? undefined : state.activeLayerId,
        };
        applyCatalogToNode(node, entry);
        const graph = state.graphs[state.activeGraphId];
        if (graph) graph.nodes.push(node);
        state.nodes = graph?.nodes ?? [...state.nodes, node];
        createdId = node.id;
      });
      return createdId;
    },

    createChildView: (nodeId, options) => {
      let pageId: string | undefined;
      applyUpdate((state) => {
        const graph = state.graphs[state.activeGraphId];
        const node = graph?.nodes.find(item => item.id === nodeId)
          ?? state.nodes.find(item => item.id === nodeId);
        if (!node) return;
        enrollNode(state, node);
        pageId = generateId('page');
        const name = options?.name?.trim() || `${node.label} — Containers`;
        const diagramType = options?.diagramType ?? 'flow';
        state.pages.push({
          id: pageId,
          name,
          description: `Child view of ${node.label}`,
          diagramType,
          nodes: [],
          edges: [],
          groups: [],
          layers: [{ ...DEFAULT_LAYER }],
          viewport: { x: 0, y: 0, scale: 1 },
          participants: [],
          messages: [],
          fragments: [],
          participantGroups: [],
          seqLifelineLength: 500,
          graphs: {
            [ROOT_GRAPH_ID]: {
              id: ROOT_GRAPH_ID,
              nodes: [],
              edges: [],
              viewport: { x: 0, y: 0, scale: 1 },
            },
          },
        });
        node.linkedPageId = pageId;
        if (graph) {
          const graphNode = graph.nodes.find(item => item.id === nodeId);
          if (graphNode) graphNode.linkedPageId = pageId;
        }
      });
      return pageId;
    },

    createC4Workspace: (systemName) => {
      const built = buildC4Workspace(systemName ?? 'System');
      applyUpdate((state) => {
        applyWorkspaceToState(state, built);
      });
      return {
        contextPageId: built.pages[0].id,
        containersPageId: built.pages[1].id,
        sequencePageId: built.pages[2].id,
      };
    },
  };
}
